import '../styles.css';
import PageHeader from '../components/PageHeader';
import IterationStatus from '../components/iterationStatus';
import LazyVideo from '../components/LazyVideo';
import { storageAssets } from '../storage_assets';

/* Typography: see styles.css - .text-header-main, .text-body, .text-body-sm */

const MediaFigure = ({ type = 'image', src, alt, caption }) => (
    <figure style={styles.figure}>
        {type === 'video' ? (
            <LazyVideo src={src} alt={alt} style={styles.image} />
        ) : (
            <img src={src} alt={alt} style={styles.image} />
        )}
        <figcaption className="text-body-sm" style={styles.imageCaption}>
            {caption}
        </figcaption>
    </figure>
);

const ProjectTenPage = () => {
    return (
        <div className="scrollable-window scrollable-window--wide">
            <div style={styles.wholeThingBox}>
                <div style={styles.boxMain}>
                    <PageHeader title="Nothing joins the meeting" />
                    <div className="text-body-sm">
                        I designed and built AskElephant&apos;s desktop recorder as the only designer and frontend engineer
                        on the project. I also handled much of the PM work. The polished version took about a month to
                        ship, building on earlier explorations from previous months. The goal was straightforward: if
                        meeting bots fail to join, AskElephant should still capture the call in a way that feels clear and
                        trustworthy. The app launched recently and is already serving 450 active users, with plans to
                        scale past 2,000 by the end of the quarter.
                    </div>
                    <div style={styles.deviderStroke}></div>

                    <div className="text-body">The problem</div>
                    <div className="text-body-sm">
                        AskElephant depends on a steady flow of meeting data. The original workflow used a meeting bot to
                        join, record, and transcribe calls. That worked only when the bot could actually get into the
                        meeting. On many platforms, bots cannot join automatically and must be admitted from the waiting
                        room. In some cases, only the host can let them in. In early 2026, Google Meet also started
                        flagging AI meeting bots more aggressively, which made that path even less reliable.
                    </div>
                    <div className="text-body-sm">
                        There was a second problem as well. Even when recording worked, users were often unsure whether the
                        meeting had ended cleanly, whether the upload had started, or whether anything had gone wrong. That
                        uncertainty mattered because AskElephant is supposed to remove mental overhead between meetings, not
                        create another thing to double-check.
                    </div>

                    <div className="text-body">The design direction</div>
                    <div className="text-body-sm">
                        The solution was a desktop recorder that captures computer audio locally. Instead of asking users to
                        trust that a third-party bot will join the call, AskElephant records from the machine they are
                        already using. That idea became the phrase that clarified the whole product: nothing joins the
                        meeting.
                    </div>
                    <div className="text-body-sm">
                        I was not only designing a tray app. I was designing a new mental model. Users already understood
                        AskElephant as a bot product, so the UX challenge was to explain local recording without making
                        people wonder whether they, the bot, or both were supposed to join the meeting.
                    </div>

                    <div className="text-body">Early ideation</div>
                    <div className="text-body-sm">
                        I started with a crazy eights exercise around a simple HMW question: how might we help users
                        understand bot recording versus local recording? That exercise helped me move quickly through
                        different control surfaces before narrowing in on the tray and notification system.
                    </div>
                    <MediaFigure
                        type="image"
                        src={storageAssets.projectTenCrazyEights}
                        alt="Journal spread with eight quick sketches from a crazy eights ideation session"
                        caption="Crazy eights used to explore different ways to explain and trigger local recording."
                    />

                    <div className="text-body">What I tested</div>
                    <div className="text-body-sm">
                        I treated the tray, notifications, and onboarding as one system and tested each major direction
                        before investing too heavily in engineering. I ran usability sessions with four external, unbiased
                        users and used internal dogfooding to catch issues that only showed up in repeated daily use. The
                        main pattern was consistent: when the product asked people to interpret too much, they hesitated.
                        The better directions were the ones that made the next step obvious.
                    </div>

                    <div className="text-body">Iteration #1</div>
                    <div className="text-body-sm">
                        My first concept centered the tray window. It opened before a meeting and tried to support several
                        jobs at once: record the upcoming calendar event, start a separate recording, or send the classic
                        AskElephant bot. In practice, that meant the UI had to explain too much at the exact moment a user
                        was trying to join a call.
                    </div>
                    <MediaFigure
                        type="image"
                        src={storageAssets.projectTenTrayV1}
                        alt="Early tray app: meeting list and multiple record and bot actions"
                        caption="Early tray concept with competing recording actions in one surface."
                    />
                    <IterationStatus
                        items={[
                            {
                                type: 'error',
                                text: 'Too many actions competed for attention in a small surface.',
                            },
                            {
                                type: 'error',
                                text: 'Users were not sure what they were supposed to do first.',
                            },
                            {
                                type: 'error',
                                text: 'Recording a meeting, starting an ad hoc recording, and sending a bot created three mental models in one place.',
                            },
                        ]}
                    />
                    <div className="text-body-sm">
                        The main learning from this round was that the tray should not be the default front door. Users did
                        better when the product chose the obvious path for them instead of presenting the whole system at
                        once.
                    </div>

                    <div className="text-body">Iteration #2</div>
                    <div className="text-body-sm">
                        The next direction made the OS notification the primary control surface. If a meeting was coming
                        up, the user could handle the common case from one clear action: join and record. The tray still
                        existed for settings, calendar context, and edge cases, but it no longer had to carry the full
                        burden of the workflow.
                    </div>
                    <MediaFigure
                        type="image"
                        src={storageAssets.projectTenNotificationV2}
                        alt="System notification with Join and record before a meeting"
                        caption="Notification-first workflow with one clear action before a meeting."
                    />
                    <IterationStatus
                        items={[
                            {
                                type: 'success',
                                text: 'The happy path lives in the native notification, so users do not need to hunt for the tray.',
                            },
                            {
                                type: 'success',
                                text: 'The tray becomes secondary instead of carrying the whole workflow.',
                            },
                            {
                                type: 'success',
                                text: 'Settings, permissions, and edge cases still have a home without cluttering the main action.',
                            },
                        ]}
                    />
                    <div className="text-body-sm">
                        One usability quote captured the confusion I needed to design around: &quot;I would expect myself
                        to join and AskElephant to join.&quot; - Doug, RevOps leader and AskElephant customer. The problem
                        was not only the button label. It was that users still carried the old bot-based mental model. The
                        new flow worked better when the surrounding UI made local recording feel like the default.
                    </div>

                    <div className="text-body">Ending the recording clearly</div>
                    <div className="text-body-sm">
                        Once local recording worked, I still needed to solve the handoff at the end of the call. If the
                        recording continued after the meeting, users felt uneasy. One internal dogfooding quote made that
                        especially clear: &quot;[I came back to my screen after and it was still recording and it] scared me
                        to death.&quot; - Wyatt, internal CSM.
                    </div>
                    <div className="text-body-sm">
                        I fixed that by ending the recording when the meeting ended and forcing the tray open with a clear
                        uploading state. That gave users immediate confirmation that the recording was safe, the upload had
                        started, and they could move on without checking for a hidden notification.
                    </div>
                    <IterationStatus
                        items={[
                            {
                                type: 'success',
                                text: 'The open tray makes the post-meeting state hard to miss.',
                            },
                            {
                                type: 'success',
                                text: 'A visible uploading state removes the need for the user to decide what to do next.',
                            },
                            {
                                type: 'success',
                                text: 'The handoff feels safer because the system shows progress instead of asking for another click.',
                            },
                            {
                                type: 'success',
                                text: 'Users get confirmation at the exact moment they need it.',
                            },
                        ]}
                    />

                    <div className="text-body">Tray behaviors</div>
                    <div className="text-body-sm">
                        Even with a notification-first workflow, the tray still needed to work well when users opened it.
                        I focused on three behaviors: choosing the right primary action, keeping the meeting list
                        manageable, and helping users return to an active recording if they had scrolled away from it.
                    </div>

                    <div className="text-body-sm">
                        <strong>Dynamic primary CTA.</strong> Users opened the tray with two different intents: either to
                        join an upcoming meeting or to start a quick recording with no meeting at all. Rather than showing
                        two competing primary actions, I changed the hierarchy based on timing. Within five minutes of a
                        calendar event, &quot;Join &amp; record&quot; becomes primary. Otherwise, &quot;New quick
                        recording&quot; takes that place.
                    </div>
                    <MediaFigure
                        type="video"
                        src={storageAssets.projectTenTrayV2DynamicCta}
                        alt="Tray demo: dynamic Join and record versus New quick recording primary button"
                        caption="Tray behavior: the primary action changes based on the next calendar event."
                    />

                    <div className="text-body-sm">
                        <strong>Meeting list hygiene.</strong> Users could manually hide or restore meetings in the list to
                        reduce clutter. It is a modest solution, but it gave people a way to control noise while we kept
                        the product simple enough to launch.
                    </div>
                    <MediaFigure
                        type="video"
                        src={storageAssets.projectTenTrayV2MeetingsManage}
                        alt="Tray demo: reverting or restoring meetings in the list"
                        caption="Tray behavior: users can hide or restore meetings to reduce clutter."
                    />

                    <div className="text-body-sm">
                        <strong>Return to the active meeting.</strong> Because the tray had limited space, it was easy to
                        scroll past the meeting that was currently recording. I added a return control so users could jump
                        back to the active meeting instead of wondering where it went.
                    </div>
                    <MediaFigure
                        type="video"
                        src={storageAssets.projectTenTrayV2ReturnMeeting}
                        alt="Tray demo: back control to return to the active recording"
                        caption="Tray behavior: return to the currently recording meeting after scrolling away."
                    />

                    <div className="text-body">Permissions onboarding</div>
                    <div className="text-body-sm">
                        After sign-in and calendar connection, macOS required microphone, screen recording, and
                        accessibility permissions. Early versions showed all three steps at the same visual weight. Testers
                        did not read that as a guided sequence. They read it as a broken set of controls.
                    </div>
                    <MediaFigure
                        type="image"
                        src={storageAssets.projectTenOnboardingPermissionsEarly}
                        alt="Early permissions UI with equal-weight steps"
                        caption="Early permissions concept with equal-weight steps."
                    />
                    <IterationStatus
                        items={[
                            {
                                type: 'error',
                                text: 'Equal visual weight made it hard to tell what was clickable.',
                            },
                            {
                                type: 'error',
                                text: 'Inactive states looked disabled instead of simply not-yet-active.',
                            },
                            {
                                type: 'error',
                                text: 'Testers said things like “There’s nowhere to click here” and “This feels like a button hover state.”',
                            },
                        ]}
                    />
                    <div className="text-body-sm">
                        A carousel version made the next click clearer, but the repeated layout made some users feel stuck
                        in a loop. A later single-page version fixed that interaction problem, but it still started too
                        abruptly. Right after authentication, users were asked for system permissions before they fully
                        understood why local recording existed.
                    </div>
                    <MediaFigure
                        type="image"
                        src={storageAssets.projectTenOnboardingPermissionsMid}
                        alt="Carousel or single-page moving primary button for permissions"
                        caption="Mid-stage onboarding concepts that improved sequencing but still felt abrupt."
                    />
                    <div className="text-body-sm">
                        The final version added an intermediary screen before the technical steps. Its job was to explain
                        the value proposition in plain language before asking for access: record locally, nothing joins the
                        meeting, and AskElephant still captures the data. I also changed the CTA to &quot;Continue to
                        permissions&quot; so the next step matched what actually happened.
                    </div>
                    <MediaFigure
                        type="video"
                        src={storageAssets.projectTenOnboardingIntermediaryFinal}
                        alt="Final intermediary onboarding screen with brand styling leading into permissions"
                        caption="Final onboarding flow with a value explanation before macOS permissions."
                    />
                    <IterationStatus
                        items={[
                            {
                                type: 'success',
                                text: 'Users understand the value proposition before they hit system dialogs.',
                            },
                            {
                                type: 'success',
                                text: 'The CTA matches the next action instead of implying the user is already entering the app.',
                            },
                            {
                                type: 'success',
                                text: 'The flow separates the product story from the permission mechanics.',
                            },
                        ]}
                    />

                    <div className="text-body">Technical constraints</div>
                    <div className="text-body-sm">
                        Several technical constraints shaped the final design. Recall powered the bot-based path, the
                        desktop recorder used chunk uploads to improve the upload experience, and the app was built in
                        React. Because I handled all frontend engineering myself, the design work stayed tightly connected
                        to implementation details throughout the project.
                    </div>

                    <div className="text-body">Outcome</div>
                    <div className="text-body-sm">
                        The final result was a desktop recording flow that reduced reliance on fragile bot admission and
                        gave users a clearer path from invite to upload. The product now has a notification-first entry
                        point, a tray that supports edge cases without carrying the whole experience, and onboarding that
                        explains local recording before asking for system permissions.
                    </div>
                    <div className="text-body-sm">
                        Because the app launched recently, I do not yet have long-term outcome data. What I do have is a
                        product now serving 450 active users, a clearer path to scaling beyond 2,000 users this quarter,
                        and a design that solves the main trust problems we saw in testing: confusion about who joins the
                        meeting, uncertainty at the end of the recording, and friction in the permissions flow.
                    </div>
                    <div className="text-body-sm">
                        The main lesson from this project is that reliability is not only a technical problem. It is also
                        a UX problem. If users do not understand what the system is doing, they will not trust it even when
                        it works.
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    wholeThingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '40px',
        alignSelf: 'stretch',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
    },
    boxMain: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '20px',
        width: 'var(--content-width)',
        maxWidth: '100%',
        minWidth: 0,
        padding: '0 clamp(1rem, 4vw, 2rem)',
        boxSizing: 'border-box',
        margin: '0',
        position: 'relative',
        zIndex: 1,
    },
    figure: {
        margin: '8px 0 0',
        alignSelf: 'stretch',
        width: '100%',
        minWidth: 0,
    },
    image: {
        width: 'var(--content-media-width)',
        height: 'auto',
        maxWidth: '100%',
        borderRadius: '12px',
        border: '1px solid var(--color-border)',
        display: 'block',
    },
    imageCaption: {
        marginTop: '10px',
        color: 'var(--color-text-secondary)',
    },
    deviderStroke: {
        height: '1px',
        backgroundColor: 'var(--color-border)',
        alignSelf: 'stretch',
    },
};

export default ProjectTenPage;
